/**
 * Business form types — match backend business-forms.js schema exactly.
 * Date strings: MM/DD/YYYY.
 */

import type { USStateCode } from "./wizard.types";

export interface BusinessTaxpayerId {
  idType: "SSN" | "EIN";
  idValue: string;
}

export interface BusinessAddress {
  street?: string;
  city?: string;
  state?: USStateCode | string;
  zip?: string;
}

/** Partner info for Partnership (1065) and S-Corp (1120-S). */
export interface PartnerInfo {
  name?: string;
  address?: string;
  street?: string;
  city?: string;
  state?: USStateCode | string;
  zip?: string;
  ssn?: string;
  partnershipPercentage?: number;
}

export interface BusinessDetails {
  businessType: string;
  proprietorName?: string;
  taxpayerId?: BusinessTaxpayerId;
  principalBusiness?: string;
  businessName?: string;
  businessAddress?: BusinessAddress;
  accountingMethod?: "CASH" | "ACCRUAL";
  materiallyParticipated?: boolean;
  startedThisYear?: boolean;
  startDate?: string;
}

export interface BusinessIncome {
  grossReceipts?: number;
  returnsAndAllowances?: number;
}

export interface BusinessExpenses {
  advertising?: number;
  carAndTruck?: number;
  commissionsAndFees?: number;
  depreciation?: number;
  employeeBenefits?: number;
  insurance?: number;
  interestMortgage?: number;
  interestOther?: number;
  legalAndProfessional?: number;
  officeExpense?: number;
  pensionProfitSharing?: number;
  rentVehicles?: number;
  rentOther?: number;
  repairsAndMaintenance?: number;
  supplies?: number;
  taxesAndLicenses?: number;
  utilities?: number;
  wages?: number;
}

export interface BusinessOtherExpenses {
  alarmMonitoring?: number;
  automobileExpensesMileage?: number;
  computersAndSupplies?: number;
  furniture?: number;
  insurance?: number;
  autoInsurance?: number;
  insurancesOther?: number;
  gas?: number;
  highwayToll?: number;
  licenseAndPermits?: number;
  misc?: number;
  officeSupplies?: number;
  rent?: number;
  repairs?: number;
  equipmentRepairs?: number;
  telephone?: number;
  internet?: number;
  travelAndEnt?: number;
  meals?: number;
  travelAndEntertainment?: number;
  utilities?: number;
  gasAndElectric?: number;
  utilitiesAndOther?: number;
  accountingFees?: number;
  totalExpenses?: number;
}

export interface CostOfGoodsSold {
  beginningInventory?: number;
  purchases?: number;
  costOfLabor?: number;
  materialsAndSupplies?: number;
  otherCosts?: number;
  endingInventory?: number;
}

export interface VehicleInfo {
  hasVehicleExpenses?: boolean;
  placedInServiceDate?: string;
  totalMiles?: number;
  businessMiles?: number;
  personalMiles?: number;
  vehicleAvailableForPersonalUse?: boolean;
  anotherVehicleAvailable?: boolean;
  hasSupportingEvidence?: boolean;
  mileageMethod?: "STANDARD" | "ACTUAL";
}

export interface FinancialInfo {
  yearEndBankBalance?: number;
  additionalNotes?: string;
}

/** Partnership details (1065 / 1120-S). */
export interface PartnershipDetails {
  numberOfPartners?: number;
  partnerInformation?: PartnerInfo[];
}

export interface BusinessFormData {
  /** Business form display name (optional, top-level). */
  formName?: string;
  businessDetails?: BusinessDetails;
  /** Partnership (1065) / S-Corp (1120-S) partner details. */
  partnershipDetails?: PartnershipDetails;
  income?: BusinessIncome;
  expenses?: BusinessExpenses;
  otherExpenses?: BusinessOtherExpenses;
  costOfGoodsSold?: CostOfGoodsSold;
  vehicleInfo?: VehicleInfo;
  financialInfo?: FinancialInfo;
}

export const BUSINESS_STEPS = [
  { id: "business-details", title: "Business Details" },
  { id: "income", title: "Income" },
  { id: "expenses", title: "Expenses" },
  { id: "cost-of-goods-sold", title: "Cost of Goods Sold" },
  { id: "vehicle", title: "Vehicle Information" },
  { id: "other-and-documents", title: "Other Expenses & Documents" },
] as const;

export type BusinessStepId = (typeof BUSINESS_STEPS)[number]["id"];
